import React from 'react';
import { useLocation, Navigate } from 'react-router-dom';

// Page components
import UserProfilePage from './pages/UserProfilePage';
import UserCollectionPage from './pages/UserCollectionPage';
import UserCollectionProductPage from './pages/UserCollectionProductPage';
import UserCollectionInsertSetPage from './pages/UserCollectionInsertSetPage';
import UserCollectionCardPage from './pages/UserCollectionCardPage';
import UserWishlistPage from './pages/UserWishlistPage';
import UserWishlistProductPage from './pages/UserWishlistProductPage';
import UserWishlistInsertSetPage from './pages/UserWishlistInsertSetPage';
import UserWishlistCardPage from './pages/UserWishlistCardPage';
import UserAvailableCardsPage from './pages/UserAvailableCardsPage';
import UserAvailableCardsProductPage from './pages/UserAvailableCardsProductPage';
import UserAvailableCardsInsertSetPage from './pages/UserAvailableCardsInsertSetPage';
import UserAvailableCardsCardPage from './pages/UserAvailableCardsCardPage';

// Define route patterns with their target components and parameter names
const routeConfigs = [
  {
    pattern: /^\/@([^/]+)\/collection\/([^/]+)\/([^/]+)\/([^/]+)$/,  // /@:username/collection/:product/:insertSet/:card
    Component: UserCollectionCardPage,
    paramNames: ['username', 'productSlug', 'insertSetSlug', 'cardSlug'],
  },
  {
    pattern: /^\/@([^/]+)\/collection\/([^/]+)\/([^/]+)$/,              // /@:username/collection/:product/:insertSet
    Component: UserCollectionInsertSetPage,
    paramNames: ['username', 'productSlug', 'insertSetSlug'],
  },
  {
    pattern: /^\/@([^/]+)\/collection\/([^/]+)$/,                        // /@:username/collection/:product
    Component: UserCollectionProductPage,
    paramNames: ['username', 'productSlug'],
  },
  {
    pattern: /^\/@([^/]+)\/collection$/,                                  // /@:username/collection
    Component: UserCollectionPage,
    paramNames: ['username'],
  },
  {
    pattern: /^\/@([^/]+)\/wishlist\/([^/]+)\/([^/]+)\/([^/]+)$/,     // /@:username/wishlist/:product/:insertSet/:card
    Component: UserWishlistCardPage,
    paramNames: ['username', 'productSlug', 'insertSetSlug', 'cardSlug'],
  },
  {
    pattern: /^\/@([^/]+)\/wishlist\/([^/]+)\/([^/]+)$/,                // /@:username/wishlist/:product/:insertSet
    Component: UserWishlistInsertSetPage,
    paramNames: ['username', 'productSlug', 'insertSetSlug'],
  },
  {
    pattern: /^\/@([^/]+)\/wishlist\/([^/]+)$/,                          // /@:username/wishlist/:product
    Component: UserWishlistProductPage,
    paramNames: ['username', 'productSlug'],
  },
  {
    pattern: /^\/@([^/]+)\/wishlist$/,                                    // /@:username/wishlist
    Component: UserWishlistPage,
    paramNames: ['username'],
  },
  {
    pattern: /^\/@([^/]+)\/available-cards\/([^/]+)\/([^/]+)\/([^/]+)$/,  // /@:username/available-cards/:product/:insertSet/:card
    Component: UserAvailableCardsCardPage,
    paramNames: ['username', 'productSlug', 'insertSetSlug', 'cardSlug'],
  },
  {
    pattern: /^\/@([^/]+)\/available-cards\/([^/]+)\/([^/]+)$/,          // /@:username/available-cards/:product/:insertSet
    Component: UserAvailableCardsInsertSetPage,
    paramNames: ['username', 'productSlug', 'insertSetSlug'],
  },
  {
    pattern: /^\/@([^/]+)\/available-cards\/([^/]+)$/,                   // /@:username/available-cards/:product
    Component: UserAvailableCardsProductPage,
    paramNames: ['username', 'productSlug'],
  },
  {
    pattern: /^\/@([^/]+)\/available-cards$/,                            // /@:username/available-cards
    Component: UserAvailableCardsPage,
    paramNames: ['username'],
  },
  {
    pattern: /^\/@([^/]+)$/,                                               // /@:username
    Component: UserProfilePage,
    paramNames: ['username'],
  },
];

export default function DynamicRouter() {
  const { pathname } = useLocation();

  // Try matching each pattern in order of specificity
  for (const { pattern, Component, paramNames } of routeConfigs) {
    const match = pathname.match(pattern);
    if (match) {
      // Build params object from regex capture groups
      const params = paramNames.reduce(
        (acc, name, index) => {
          // Convert username parameters to lowercase
          const value = match[index + 1];
          if (name === 'username') {
            return { ...acc, [name]: value.toLowerCase() };
          }
          return { ...acc, [name]: value };
        },
        {}
      );
      return <Component {...params} />;
    }
  }

  // If nothing matches, redirect to login (or show 404)
  return <Navigate to="/login" replace />;
}
